import React, { useState, useEffect, useCallback } from "react";
import "./ErrorOverlay.scss";

interface ErrorEntry {
    id: string;
    timestamp: string;
    message: string;
    stack?: string;
    type: "error" | "warning" | "info";
    count: number;
}

/**
 * Safely stringify objects, handling circular references and errors
 */
const safeStringify = (arg: any): string => {
    if (arg === null) return "null";
    if (arg === undefined) return "undefined";
    if (typeof arg !== "object") return String(arg);

    try {
        const seen = new WeakSet();
        return JSON.stringify(
            arg,
            (_key, value) => {
                if (typeof value === "object" && value !== null) {
                    if (seen.has(value)) {
                        return "[Circular]";
                    }
                    seen.add(value);
                }
                return value;
            },
            2
        );
    } catch (e) {
        return `[Object: ${Object.prototype.toString.call(arg)}]`;
    }
};

export interface ErrorOverlayProps {
    /**
     * Enable error overlay for debugging. Useful for React Native where console access is limited.
     * Can be overridden by localStorage setting 'xaErrorOverlay' = 'enabled'/'disabled'.
     */
    enableErrorOverlay?: boolean;
}

export const ErrorOverlay: React.FC<ErrorOverlayProps> = ({ enableErrorOverlay }) => {
    const [errors, setErrors] = useState<ErrorEntry[]>([]);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [isEnabled, setIsEnabled] = useState(false);
    const [position, setPosition] = useState<"top" | "bottom">("bottom");

    // Check if error overlay should be enabled and load position
    useEffect(() => {
        const checkEnabled = () => {
            if (typeof window === "undefined") return false;

            // 1. Check localStorage (highest priority)
            const localStorageSetting = window.localStorage?.getItem("xaErrorOverlay");
            if (localStorageSetting === "enabled") {
                console.log("[ErrorOverlay] Enabled via localStorage");
                return true;
            }
            if (localStorageSetting === "disabled") {
                console.log("[ErrorOverlay] Disabled via localStorage");
                return false;
            }

            // 2. Check widget configuration
            if (enableErrorOverlay !== undefined) {
                console.log("[ErrorOverlay] Using config setting:", enableErrorOverlay);
                return enableErrorOverlay;
            }

            // 3. Check for React Native dev mode
            if (typeof (globalThis as any).__DEV__ !== "undefined" && (globalThis as any).__DEV__) {
                console.log("[ErrorOverlay] Enabled via __DEV__");
                return true;
            }

            // 4. Default: disabled
            return false;
        };

        const enabled = checkEnabled();
        setIsEnabled(enabled);

        // Load position preference
        const savedPosition = window.localStorage?.getItem("xaErrorOverlayPosition") as
            | "top"
            | "bottom";
        if (savedPosition === "top" || savedPosition === "bottom") {
            setPosition(savedPosition);
        }

        console.log(
            "[ErrorOverlay] Component mounted, enabled:",
            enabled,
            "position:",
            savedPosition || "bottom"
        );
    }, [enableErrorOverlay]);

    // Define addError with useCallback to prevent stale closures
    const addError = useCallback((error: Omit<ErrorEntry, "id" | "timestamp" | "count">) => {
        console.log("[ErrorOverlay] Adding error:", error);
        setErrors((prev) => {
            const timestamp = new Date().toISOString();

            // Check if this is a duplicate of the last error
            const lastError = prev[prev.length - 1];
            if (lastError && lastError.message === error.message) {
                // Update count of last error
                return [
                    ...prev.slice(0, -1),
                    { ...lastError, count: lastError.count + 1, timestamp },
                ];
            }

            // Add new error
            const newError: ErrorEntry = {
                id: `${timestamp}-${Math.random()}`,
                timestamp,
                count: 1,
                ...error,
            };

            // Keep only last 50 errors
            const updated = [...prev, newError];
            return updated.slice(-50);
        });
    }, []);

    useEffect(() => {
        if (!isEnabled) {
            console.log("[ErrorOverlay] Not enabled, skipping error interception");
            return undefined;
        }

        console.log("[ErrorOverlay] Setting up error interception");

        // Store original console methods
        const originalError = console.error;
        const originalWarn = console.warn;

        // Override console.error
        console.error = (...args: any[]) => {
            const message = args.map((arg) => safeStringify(arg)).join(" ");

            const error = new Error();
            addError({
                message,
                type: "error",
                stack: error.stack,
            });

            // Call original method
            originalError.apply(console, args);
        };

        // Override console.warn
        console.warn = (...args: any[]) => {
            const message = args.map((arg) => safeStringify(arg)).join(" ");

            addError({
                message,
                type: "warning",
            });

            // Call original method
            originalWarn.apply(console, args);
        };

        // Capture unhandled errors
        const handleError = (event: ErrorEvent) => {
            addError({
                message: event.message,
                type: "error",
                stack: event.error?.stack,
            });
        };

        // Capture unhandled promise rejections
        const handleRejection = (event: PromiseRejectionEvent) => {
            addError({
                message: `Unhandled Promise Rejection: ${event.reason}`,
                type: "error",
                stack: event.reason?.stack,
            });
        };

        window.addEventListener("error", handleError);
        window.addEventListener("unhandledrejection", handleRejection);

        // Cleanup
        return () => {
            console.log("[ErrorOverlay] Cleaning up error interception");
            console.error = originalError;
            console.warn = originalWarn;
            window.removeEventListener("error", handleError);
            window.removeEventListener("unhandledrejection", handleRejection);
        };
    }, [isEnabled, addError]);

    const clearErrors = () => {
        setErrors([]);
    };

    const copyToClipboard = async () => {
        const errorText = errors
            .map(
                (e) =>
                    `[${e.timestamp}] ${e.type.toUpperCase()}: ${e.message}${
                        e.stack ? "\n" + e.stack : ""
                    }`
            )
            .join("\n\n");

        try {
            await navigator.clipboard.writeText(errorText);
            console.log("[ErrorOverlay] Errors copied to clipboard");
        } catch (err) {
            console.error("[ErrorOverlay] Failed to copy to clipboard:", err);
            // Fallback: create a temporary textarea and use document.execCommand
            const textarea = document.createElement("textarea");
            textarea.value = errorText;
            textarea.style.position = "fixed";
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand("copy");
                console.log("[ErrorOverlay] Errors copied to clipboard using fallback method");
            } catch (fallbackErr) {
                console.error("[ErrorOverlay] Fallback copy method also failed:", fallbackErr);
            }
            document.body.removeChild(textarea);
        }
    };

    // Toggle enabled state
    const toggleEnabled = () => {
        const newState = !isEnabled;
        setIsEnabled(newState);

        if (typeof window !== "undefined" && window.localStorage) {
            window.localStorage.setItem("xaErrorOverlay", newState ? "enabled" : "disabled");
        }

        console.log("[ErrorOverlay] Toggled enabled state to:", newState);

        // Clear errors when disabling
        if (!newState) {
            setErrors([]);
        }
    };

    // Toggle position between top and bottom
    const togglePosition = () => {
        const newPosition = position === "bottom" ? "top" : "bottom";
        setPosition(newPosition);

        if (typeof window !== "undefined" && window.localStorage) {
            window.localStorage.setItem("xaErrorOverlayPosition", newPosition);
        }
    };

    // Don't render anything if not enabled
    if (!isEnabled) {
        return null;
    }

    // If enabled but not visible (user closed it), don't render
    if (!isVisible) {
        return null;
    }

    // If enabled and visible but no errors yet, don't show the overlay
    // Only show when there are actual errors to display
    if (errors.length === 0) {
        return null;
    }

    return (
        <div
            className={`error-overlay error-overlay--${position} ${
                isMinimized ? "minimized" : ""
            }`}>
            <div className="error-overlay-header">
                <span className="error-overlay-title">Debug Console ({errors.length})</span>
                <div className="error-overlay-actions">
                    <button onClick={copyToClipboard} title="Copy all to clipboard">
                        📋
                    </button>
                    <button onClick={clearErrors} title="Clear all">
                        🗑️
                    </button>
                    <button
                        onClick={togglePosition}
                        title={`Move to ${position === "bottom" ? "top" : "bottom"}`}>
                        {position === "bottom" ? "⬆️" : "⬇️"}
                    </button>
                    <button onClick={toggleEnabled} title="Disable Error Overlay">
                        🔧
                    </button>
                    <button onClick={() => setIsMinimized(!isMinimized)} title="Minimize/Maximize">
                        {isMinimized ? "▲" : "▼"}
                    </button>
                    <button onClick={() => setIsVisible(false)} title="Close">
                        ✕
                    </button>
                </div>
            </div>

            {!isMinimized && (
                <div className="error-overlay-content">
                    {errors.map((error) => (
                        <div key={error.id} className={`error-entry error-${error.type}`}>
                            <div className="error-header">
                                <span className="error-time">
                                    {new Date(error.timestamp).toLocaleTimeString()}
                                </span>
                                {error.count > 1 && (
                                    <span className="error-count">({error.count}x)</span>
                                )}
                                <span className={`error-badge error-badge-${error.type}`}>
                                    {error.type}
                                </span>
                            </div>
                            <div className="error-message">{error.message}</div>
                            {error.stack && (
                                <details className="error-stack">
                                    <summary>Stack trace</summary>
                                    <pre>{error.stack}</pre>
                                </details>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
